import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Edit3,
  Trash2,
  MoreVertical,
  Building2,
  Clock,
  Star,
  Navigation,
  Globe,
  Eye,
  ExternalLink
} from "lucide-react";

const BranchCard = ({ 
  branch, 
  onEdit, 
  onDelete, 
  onViewDetails,
  isDeleting, 
  canEdit, 
  canDelete, 
  canViewDetails = true,
  viewMode = "grid",
  index = 0 
}) => {
  const [showActions, setShowActions] = React.useState(false);
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowActions(false);
      }
    };

    if (showActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActions]);

  const handleEdit = () => {
    setShowActions(false);
    onEdit(branch);
  };

  const handleDelete = () => {
    setShowActions(false);
    onDelete(branch);
  };

  const handleViewDetails = () => {
    setShowActions(false);
    onViewDetails(branch);
  };

  if (viewMode === "list") {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
        className="relative p-6 bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 group hover:shadow-lg hover:border-blue-200"
      >
        <div className="flex justify-between items-center">
          <div className="flex flex-1 gap-4 items-center">
            {/* Branch Icon */}
            <div className="p-3 text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
              <Building2 className="w-6 h-6" />
            </div>

            {/* Branch Info */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {branch.name}
              </h3>
              <div className="flex gap-4 items-center mt-1 text-sm text-gray-600">
                <div className="flex gap-1 items-center">
                  <MapPin className="w-4 h-4 text-blue-500" />
                  <span>{branch.region} - {branch.location}</span>
                </div>
                {branch.phone_number && (
                  <div className="flex gap-1 items-center">
                    <Phone className="w-4 h-4 text-green-500" />
                    <span>{branch.phone_number}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Status Badge */}
            <div className="flex gap-2 items-center">
              <div className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                Active
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 items-center">
            {canViewDetails && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleViewDetails}
                className="p-2 text-gray-400 rounded-lg transition-colors hover:text-blue-600 hover:bg-blue-50"
              >
                <Eye className="w-4 h-4" />
              </motion.button>
            )}
            {canEdit && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleEdit}
                className="p-2 text-gray-400 hover:text-[#FF6F6E] hover:bg-[#FF6F6E]/10 rounded-lg transition-colors"
              >
                <Edit3 className="w-4 h-4" />
              </motion.button>
            )}
            {canDelete && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-2 text-gray-400 rounded-lg transition-colors hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex overflow-hidden relative flex-col p-6 h-full bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 group hover:shadow-xl hover:border-blue-200"
    >
      {/* Background Pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-transparent rounded-full opacity-50 translate-x-16 -translate-y-16" />
      
      {/* Header */}
      <div className="flex relative justify-between items-start mb-4">
        <div className="flex gap-3 items-center">
            <div className="p-3 rounded-xl bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] text-white shadow-lg">
              <Building2 className="w-6 h-6" />
            </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#FF6F6E] transition-colors">
              {branch.name}
            </h3>
            {branch.google_maps_link ? (
              <div className="flex gap-1 items-center mt-1">
                <ExternalLink className="w-4 h-4 text-blue-500" />
                <a 
                  href={branch.google_maps_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 transition-colors hover:text-blue-800"
                >
                  View on Maps
                </a>
              </div>
            ) : (
              <div className="flex gap-1 items-center mt-1">
                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                <span className="text-sm text-gray-500">Primary Location</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
          >
            <MoreVertical className="w-4 h-4" />
          </motion.button>

          {showActions && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="absolute right-0 top-full z-10 mt-2 w-48 bg-white rounded-xl border border-gray-100 shadow-lg"
            >
              <div className="p-2">
                {canViewDetails && (
                  <button
                    onClick={handleViewDetails}
                    className="flex gap-3 items-center px-3 py-2 w-full text-gray-700 rounded-lg transition-colors hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View Details</span>
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={handleEdit}
                    className="flex gap-3 items-center px-3 py-2 w-full text-gray-700 rounded-lg transition-colors hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>Edit Branch</span>
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="flex gap-3 items-center px-3 py-2 w-full text-gray-700 rounded-lg transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>{isDeleting ? "Deleting..." : "Delete Branch"}</span>
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Location Info */}
      <div className="flex-grow mb-4 space-y-3">
        <div className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl">
            <div className="p-2 rounded-lg bg-[#FF6F6E]/10">
              <MapPin className="w-4 h-4 text-[#FF6F6E]" />
            </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{branch.location}</p>
            <p className="text-xs text-gray-500">{branch.region}</p>
          </div>
        </div>

        {branch.phone_number && (
          <div className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl">
            <div className="p-2 rounded-lg bg-[#FF9A99]/10">
              <Phone className="w-4 h-4 text-[#FF9A99]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{branch.phone_number}</p>
              <p className="text-xs text-gray-500">Contact Number</p>
            </div>
          </div>
        )}

        {branch.google_maps_link && (
          <div className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl">
            <div className="p-2 rounded-lg bg-[#FF8584]/10">
              <Navigation className="w-4 h-4 text-[#FF8584]" />
            </div>
            <div className="flex-1">
              <a 
                href={branch.google_maps_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full text-sm font-medium text-[#FF6F6E] hover:text-[#E55A59] transition-colors underline"
              >
                View on Google Maps
              </a>
              <p className="text-xs text-gray-500">Navigation Link</p>
            </div>
          </div>
        )}

        {/* Additional Details */}
        {(branch.district || branch.suburb || branch.landmark) && (
          <div className="space-y-2">
            {branch.district && (
              <div className="flex gap-2 items-center text-sm text-gray-600">
                <Navigation className="w-4 h-4 text-gray-400" />
                <span>District: {branch.district}</span>
              </div>
            )}
            {branch.suburb && (
              <div className="flex gap-2 items-center text-sm text-gray-600">
                <Globe className="w-4 h-4 text-gray-400" />
                <span>Suburb: {branch.suburb}</span>
              </div>
            )}
            {branch.landmark && (
              <div className="flex gap-2 items-center text-sm text-gray-600">
                <Star className="w-4 h-4 text-gray-400" />
                <span>Landmark: {branch.landmark}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center pt-4 mt-auto border-t border-gray-100">
        <div className="flex gap-2 items-center text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          <span>Added recently</span>
        </div>
        <div className="px-3 py-1 bg-[#FF8584]/10 text-[#FF8584] rounded-full text-xs font-medium">
          Active
        </div>
      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#FF6F6E]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </motion.div>
  );
};

export default BranchCard;
